import { S } from '../core/game';
import { bus } from '../core/bus';
import { toast } from '../core/effects';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import type { Marker } from '../render/renderer';
import { getObject, MAPS, type MapId } from '../world/map';
import { npcDef } from '../world/npcs';
import { T } from '../world/tiles';
import type { World } from '../world/world';
import { todaysObjectives } from './days';
import { isFound, todaysSpots } from './treasure';
import {
  advance, CLOVERS, collectClover, completeQuest, isActive, KITTEN_BUSHES, LAP_POINTS, LAP_TIME, questDef, step, type QuestId, type TargetRef,
} from './quests';

/** What each daily goal points at. */
const OBJECTIVE_TARGETS: Record<string, TargetRef | null> = {
  xiaoyu: { kind: 'npc', id: 'xiaoyu' },
  teacher: { kind: 'npc', id: 'teacher' },
  log: { kind: 'obj', id: 'desk' },
  homework: { kind: 'obj', id: 'homeDesk' },
  cookies: { kind: 'obj', id: 'oven' },
  picnic: { kind: 'npc', id: 'emma' },
  dishes: { kind: 'obj', id: 'sink' },
  sister: { kind: 'npc', id: 'sister' },
  strawberries: { kind: 'npc', id: 'clerk' },
  cake: { kind: 'obj', id: 'oven' },
  giveCake: { kind: 'npc', id: 'mom' },
  callDad: null,
};

function resolve(ref: TargetRef, world: World): { map: MapId; x: number; y: number } | null {
  const s = S();
  if (ref.kind === 'pos') return ref;
  if (ref.kind === 'obj') {
    const o = getObject(ref.id);
    return o ? { map: o.map, x: o.rect.x + o.rect.w / 2, y: o.rect.y + o.rect.h } : null;
  }
  const spot = npcDef(ref.id).where(s);
  if (!spot) return null;
  const live = spot.map === world.map.id ? world.npcPosition(ref.id) : null;
  return { map: spot.map, x: live?.x ?? spot.x, y: live?.y ?? spot.y };
}

/**
 * Where to go next to reach another map: doors, plus the train, the MRT and the
 * Hualien shuttle. Breadth-first over the map graph, so the arrow can point from
 * home all the way to a beach in Hualien, one portal at a time.
 */
interface Portal {
  to: MapId;
  at: { x: number; y: number };
}
const DISTRICTS: MapId[] = ['ximen', 'longshan', 'cks', 'beitou', 'museum', 'mrt'];
let portalCache: Map<MapId, Portal[]> | null = null;
function portals(): Map<MapId, Portal[]> {
  if (portalCache) return portalCache;
  const g = new Map<MapId, Portal[]>();
  const add = (from: MapId, to: MapId, at: { x: number; y: number } | null) => {
    if (!at) return;
    if (!g.has(from)) g.set(from, []);
    g.get(from)!.push({ to, at });
  };
  const obj = (id: string) => {
    const o = getObject(id as never);
    return o ? { x: o.rect.x + o.rect.w / 2, y: o.rect.y + o.rect.h } : null;
  };
  const spot = (map: MapId, key: string) => {
    const sp = MAPS[map].spots?.[key];
    return sp ? { x: sp.x, y: sp.y } : null;
  };
  for (const map of Object.values(MAPS)) {
    for (const d of map.doors) {
      const [tx, ty] = d.tiles[0];
      add(map.id, d.to, { x: tx * T + (d.tiles.length > 1 ? T : T / 2), y: ty * T + T / 2 });
    }
  }
  add('station', 'taipei', obj('stTrain'));
  add('station', 'hlcity', obj('stTrain'));
  add('taipei', 'station', obj('tpTrain'));
  for (const d of DISTRICTS) add('taipei', d, obj('xy_mrtSys'));
  for (const d of DISTRICTS) {
    const at = d === 'museum' ? obj('muExit') : d === 'mrt' ? obj('mrtRide') : spot(d, 'mrt');
    add(d, 'taipei', at);
    for (const o of DISTRICTS) if (o !== d) add(d, o, at);
  }
  add('hlcity', 'station', obj('hlTrain'));
  add('hlcity', 'hualien', obj('hc_shuttle'));
  add('hualien', 'hlcity', obj('tk_shuttleTaroko'));
  // Phase F: high-speed rail to the south, the Airport MRT and the flight to Barcelona.
  add('station', 'kaohsiung', obj('stTrain'));
  add('station', 'tainan', obj('stTrain'));
  add('kaohsiung', 'station', obj('kh_hsr'));
  add('tainan', 'station', obj('tn_hsr'));
  add('taipei', 'airport', obj('ap_mrtTaipei'));
  add('airport', 'taipei', obj('ap_mrt'));
  add('airport', 'barcelona', obj('ap_gate'));
  add('barcelona', 'airport', obj('bc_flight'));
  portalCache = g;
  return g;
}

function doorTowards(from: MapId, to: MapId): { x: number; y: number } | null {
  const g = portals();
  const prev = new Map<MapId, { via: MapId; at: { x: number; y: number } }>();
  const queue: MapId[] = [from];
  const seen = new Set<MapId>([from]);
  while (queue.length) {
    const cur = queue.shift()!;
    if (cur === to) break;
    for (const p of g.get(cur) ?? []) {
      if (seen.has(p.to)) continue;
      seen.add(p.to);
      prev.set(p.to, { via: cur, at: p.at });
      queue.push(p.to);
    }
  }
  if (!prev.has(to)) return null;
  // Walk back to the first hop out of `from`.
  let cur = to;
  let hop = prev.get(cur)!;
  while (hop.via !== from) {
    cur = hop.via;
    hop = prev.get(cur)!;
  }
  return hop.at;
}

/** A place the player picked on the map: the arrow leads there until they arrive. */
let pin: { map: MapId; x: number; y: number; label: string } | null = null;
export function setPin(p: { map: MapId; x: number; y: number; label: string } | null): void {
  pin = p;
  bus.emit('change');
}
export function currentPin(): { map: MapId; x: number; y: number; label: string } | null {
  return pin;
}

/** Current guide target: the map pin, else the tracked quest, else the first open daily goal. */
export function guideTarget(world: World): { x: number; y: number } | null {
  const s = S();
  let ref: TargetRef | null = null;
  if (pin) {
    if (pin.map === world.map.id && Math.hypot(pin.x - world.player.x, pin.y - world.player.y) < 26) {
      toast(m().airport.mapUi.pinCleared);
      pin = null;
      bus.emit('change');
    } else ref = { kind: 'pos', map: pin.map, x: pin.x, y: pin.y };
  }
  if (!ref && s.tracked && isActive(s.tracked as QuestId)) ref = questDef(s.tracked as QuestId).target(step(s.tracked as QuestId), s);
  if (!ref) {
    const open = todaysObjectives(s).find((o) => !o.done && OBJECTIVE_TARGETS[o.key]);
    if (open) ref = OBJECTIVE_TARGETS[open.key];
  }
  if (!ref) return null;
  const where = resolve(ref, world);
  if (!where) return null;
  if (where.map === world.map.id) return { x: where.x, y: where.y };
  return doorTowards(world.map.id, where.map);
}

export function markers(world: World): Marker[] {
  const s = S();
  const out: Marker[] = [];
  CLOVERS.forEach((c, i) => {
    if (c.map === world.map.id && !s.forever[`clover.${i}`]) out.push({ kind: 'clover', x: c.x, y: c.y });
  });
  if (world.map.id === 'town' && isActive('laps') && step('laps') === 1) {
    const i = s.quests.laps!.data ?? 0;
    const p = LAP_POINTS[i];
    if (p) out.push({ kind: 'checkpoint', x: p.x, y: p.y, label: `${i + 1}/${LAP_POINTS.length}` });
  }
  if (world.map.id === 'town' && isActive('kitten') && step('kitten') === 1) {
    const bush = getObject(KITTEN_BUSHES[s.quests.kitten!.data ?? 0]);
    if (bush) out.push({ kind: 'rustle', x: bush.rect.x + bush.rect.w / 2, y: bush.rect.y + bush.rect.h });
  }
  // Treasure glints show up only when you're close (exploring is the fun part).
  const p = world.player;
  for (const sp of todaysSpots()) {
    if (sp.map === world.map.id && !isFound(sp.id) && Math.hypot(sp.x - p.x, sp.y - p.y) < 60) out.push({ kind: 'glint', x: sp.x, y: sp.y });
  }
  return out;
}

let lapClock = 0;

/** Per-frame quest rules that depend on where the player is. */
export function questTick(dt: number, world: World): void {
  const s = S();
  const p = world.player;
  CLOVERS.forEach((c, i) => {
    if (c.map === world.map.id && !s.forever[`clover.${i}`] && Math.hypot(c.x - p.x, c.y - p.y) < 12) {
      sfx('pickup');
      world.fx.burst('spark', c.x, c.y - 6, 10);
      collectClover(i);
    }
  });

  if (isActive('laps') && step('laps') === 1) {
    const q = s.quests.laps!;
    lapClock += dt;
    const i = q.data ?? 0;
    const cp = LAP_POINTS[i];
    if (world.map.id === 'town' && cp && Math.hypot(cp.x - p.x, cp.y - p.y) < 18) {
      q.data = i + 1;
      sfx('pickup');
      world.fx.burst('spark', cp.x, cp.y - 16, 8);
      if (q.data >= LAP_POINTS.length) {
        advance('laps', 2);
        toast(m().quests.lapsDone(Math.round(lapClock)));
      } else bus.emit('change');
    }
    if (lapClock > LAP_TIME) {
      q.step = 0;
      q.data = 0;
      lapClock = 0;
      sfx('bad');
      toast(m().quests.lapsFail);
      bus.emit('change');
    }
  } else lapClock = 0;

  if (isActive('study') && step('study') === 1 && (s.counters.wordsToday ?? 0) >= 3) advance('study', 2);
}

/** Restart the lap timer (when the coach starts the challenge). */
export function resetLapClock(): void {
  lapClock = 0;
}

export function lapSecondsLeft(): number {
  return Math.max(0, Math.ceil(LAP_TIME - lapClock));
}

export { completeQuest };
